import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { SupportCase } from '../models/support-case.model';
import { AppConstant } from '../utilities/app.constant';
import { AppHelper } from '../utilities/app.helper';
import { BaseService } from './base.service';

@Injectable({
  providedIn: 'root',
})
export class ChatService extends BaseService {
  public baseUrl = 'live-support';

  constructor(httpClient: HttpClient) {
    super(httpClient);
  }

  public getMyMessages(take: number, skip: number): Observable<any[]> {
    return this.get<any[]>(`${this.baseUrl}/messages`, {
      params: {
        take,
        skip,
      },
    }).pipe(
      map((res: any) => {
        return res!.data;
      })
    );
  }

  public sendMessage(message: Partial<AppConstant.Message>): Observable<any> {
    const payload = new FormData();
    payload.append(
      'content',
      AppHelper.UtileFunctions.encodeBase64(message.content || '')
    );
    payload.append('type', message?.type || '');
    payload.append('senderType', message.senderType || '');

    if (message?.attachments)
      message?.attachments.forEach((file: File) => {
        const fileName = AppHelper.UtileFunctions.encodeFileName(file.name);
        payload.append('supportCaseAttachments', file, fileName);
      });

    return this.post<any, any>(`${this.baseUrl}/messages`, payload).pipe(
      map((res: any) => {
        return res!.data;
      })
    );
  }

  public getUnreadLiveMessages(): Observable<number> {
    return this.get<any[]>(`${this.baseUrl}/messages/unread`).pipe(
      map((res: any) => {
        return res!.data;
      })
    );
  }

  public readAllLiveMessages(): Observable<any[]> {
    return this.patch<any, any>(`${this.baseUrl}/messages/read-all`).pipe(
      map((res: any) => {
        return res!.data;
      })
    );
  }

  public readLiveMessage(messageId: number): Observable<any[]> {
    return this.patch<any, any>(
      `${this.baseUrl}/messages/${messageId}/read`
    ).pipe(
      map((res: any) => {
        return res!.data;
      })
    );
  }

  public addEvent(eventType: AppConstant.SupportCaseEvents): Observable<any> {
    return this.post<any, any>(`${this.baseUrl}/messages/events`, {
      type: eventType,
    }).pipe(
      map((res: any) => {
        return res!.data;
      })
    );
  }

  public updateInteractionMesssage(
    messageId: number,
    updatedContent: any,
    selectedButtonLavel?: string,
    selectedActionButton?: AppConstant.ActionButton
  ): Observable<any> {
    return this.post<any, any>(
      `${this.baseUrl}/messages/${messageId}/update-interaction`,
      {
        content: updatedContent,
        selectedButtonLavel,
        selectedActionButton,
      }
    ).pipe(
      map((res: any) => {
        return res!.data;
      })
    );
  }

  public translateMessage(
    messageId: number,
    targetLanguage: string
  ): Observable<any> {
    return this.post<any, any>(
      `${this.baseUrl}/messages/${messageId}/translate`,
      {},
      {
        params: {
          targetLanguage,
        },
      }
    ).pipe(
      map((res: any) => {
        return res!.data;
      })
    );
  }

  public getCurrentSupportCase(): Observable<SupportCase> {
    return this.get<any>(`${this.baseUrl}/current-support-case`).pipe(
      map((res: any) => {
        return res!.data;
      })
    );
  }

  public sendWebhookNotice(content: any): Observable<unknown> {
    return this.post<unknown, any>(
      `notification_events/teams-notification`,
      content
    );
  }

  /**
   * Unified function to send agent notification for different scenarios
   * @param userInfo The current user information
   * @param context Context message explaining the scenario (e.g. "User clicked 'Chat with human' button")
   * @param userMessage Optional user message that triggered the notification
   * @returns Observable of the webhook response
   */
  public sendAgentNotification(
    userInfo: any,
    context: string,
    userMessage?: string
  ): Observable<unknown> {
    // Format the user message section if provided
    const messageSection = userMessage
      ? `
      <p style="font-size: 16px; margin: 5px 0;">
        <strong>💬 User message:</strong> "${userMessage}"
      </p>
    `
      : '';

    // Create a formatted message with enhanced styling and icons
    const formattedMessage = `
      <div style="border-left: 4px solid #c41230; padding-left: 10px; margin: 10px 0; font-family: Arial, sans-serif;">
        <h2 style="color: #c41230; margin-bottom: 10px;">
          🚨 <strong>USER NEED CONTACT WITH AGENT</strong>
        </h2>
        
        <p style="font-size: 16px; margin: 5px 0;">
          <strong>👤 User:</strong> ${userInfo.displayName} (<a href="mailto:${userInfo.mail}">${userInfo.mail}</a>)
        </p>
        <p style="font-size: 16px; margin: 5px 0;">
          <strong>📱 Application:</strong> ${AppConstant.APPLICATION_NAME}
        </p>
        ${messageSection}
        <p style="font-size: 16px; margin: 5px 0;">
          <strong>⏰ Timestamp:</strong> ${new Date().toLocaleString()}
        </p>
        <p style="font-size: 16px; margin: 5px 0;">
          <strong>📝 Context:</strong> ${context}
        </p>
        <p style="margin-top: 10px;">
          <a href="${environment.APP_URL}" style="background-color: #c41230; color: white; padding: 6px 12px; text-decoration: none; display: inline-block; font-weight: bold;">
            OPEN LIVE SUPPORT
          </a>
        </p>
      </div>
    `;

    // Create the content with both formats for different rendering scenarios
    const content = {
      // Add the required 'message' property that Logic App is looking for
      message: formattedMessage,

      // Keep the structured format for services that support it
      title: 'USER NEED CONTACT WITH AGENT',
      themeColor: '#c41230',
      sections: [
        {
          activityTitle: '🚨 USER NEED CONTACT WITH AGENT',
          activitySubtitle: `${userInfo.displayName} (<a href='mailto:${userInfo.mail}'>${userInfo.mail}</a>) needs assistance`,
          facts: [
            { name: 'Application', value: AppConstant.APPLICATION_NAME },
            { name: 'Timestamp', value: new Date().toLocaleString() },
            { name: 'Context', value: context },
            ...(userMessage
              ? [{ name: 'User message', value: `"${userMessage}"` }]
              : []),
          ],
          markdown: true,
          potentialAction: [
            {
              '@type': 'OpenUri',
              name: 'OPEN LIVE SUPPORT',
              targets: [{ os: 'default', uri: environment.APP_URL }],
            },
          ],
        },
      ],
    };

    return this.sendWebhookNotice(content);
  }
}
